import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, firstValueFrom, map, of, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  FeatureFlag,
  NavFlag,
  Environment,
  ENV_KEY_MAP,
} from './feature-flags.model';
import { Command } from 'app/models/command';



@Injectable({ providedIn: 'root' })
export class FeatureFlagService {

  private readonly apiServer = environment.siteReviewURL;
  private readonly sessionId = localStorage.getItem("sr_session_id");
  private readonly currentEnv: Environment = environment.env; // e.g. 'alpha'

  // ── Nav flags — loaded once at boot, read by router and nav
  // Private so nothing outside the service mutates it directly.
  private _navFlags: NavFlag[] = [];

  // Expose as readonly so router/nav can read but not write
  get navFlags(): readonly NavFlag[] { return this._navFlags; }

  constructor(private http: HttpClient) {}

  // ── URL builder 
  private buildUrl(procedure: string): string {
    return `${this.apiServer}/api/cmd?tracker=${procedure}&sid=${this.sessionId}`;
  }

  private normalise(raw: any): FeatureFlag {
    return {
      ...raw,
      development: Boolean(raw.development),
      alpha:       Boolean(raw.alpha),
      beta:        Boolean(raw.beta),
      production:  Boolean(raw.production),
    };
  }

  private normaliseNav(raw: any): NavFlag {
    return {
      code:        raw.code,
      development: Boolean(raw.development),
      alpha:       Boolean(raw.alpha),
      beta:        Boolean(raw.beta),
      production:  Boolean(raw.production),
    };
  }

  // ── BOOT: called by APP_INITIALIZER 

  loadNavFlags(): Promise<void> {
    if (!this.sessionId) {
      // Not logged in yet — flags will be loaded after OAuth completes
      return Promise.resolve();
    }
    
    const cmd = new Command();
    cmd.procedure = 'cmdFeaturesNav';
    cmd.addParameter('Environment', this.currentEnv);

    return firstValueFrom(
      this.http.post<NavFlag[]>(this.buildUrl(cmd.procedure), cmd).pipe(
        map(res => {
          this._navFlags = (res ?? []).map(f => this.normaliseNav(f));
        }),
        catchError(err => {
          console.warn('[FeatureFlagService] cmdFeaturesNav failed — all flags disabled.', err);
          this._navFlags = [];
          return of(undefined);
        })
      )
    );
  }

  // ── Check if a feature code is enabled for the current environment 
  // Used by: route resolver, nav filter, feature directives
  isEnabled(code: string): boolean {
    const flag = this._navFlags.find(f => f.code === code);
    if (!flag) return false;
    return flag[ENV_KEY_MAP[this.currentEnv]];
  }

  // ── Reload nav flags (call after a toggle/promote in the admin UI) 
  refreshNavFlags(): Promise<void> {
    return this.loadNavFlags();
  }

  // ── ADMIN: load full flag list for the UI matrix
  loadData(): Observable<FeatureFlag[]> {
    const cmd = new Command();
    cmd.procedure = 'cmdFeatures';
    cmd.addParameter('Environment', '');

    return this.http
      .post<FeatureFlag[]>(this.buildUrl(cmd.procedure), cmd)
      .pipe(
        map((res: any) => (res ?? []).map(f => this.normalise(f))),
        catchError(err => throwError(() => err))
      );
  }

  // ── ADMIN: toggle a single environment on a flag 
  toggleFlag(featureID: number | string, env: Environment, enabled: boolean): Observable<any> {
    const cmd = new Command();
    cmd.procedure = 'cmdFeaturesToggle';
    cmd.addParameter('FeatureID', featureID);
    cmd.addParameter('Environment', env);
    cmd.addParameter('Enabled', enabled ? 1 : 0);

    return this.http
      .post(this.buildUrl(cmd.procedure), cmd)
      .pipe(
        map((res: any) => res),
        catchError(err => throwError(() => err))
      );
  }

  // ── ADMIN: create a new flag 
  createFlag(payload: Partial<FeatureFlag>): Observable<FeatureFlag> {
    const cmd = new Command();
    cmd.procedure = 'cmdFeaturesCreate';
    cmd.addParameter('FeatureName',   payload.featureName);
    cmd.addParameter('Code',          payload.code);
    cmd.addParameter('Branch',        payload.branch   ?? null);
    cmd.addParameter('WorkPackageID', payload.workPackageID ?? null);
    cmd.addParameter('Status',        payload.status   ?? 'Active');

    return this.http
      .post<FeatureFlag>(this.buildUrl(cmd.procedure), cmd)
      .pipe(
        map((res: any) => this.normalise(res)),
        catchError(err => throwError(() => err))
      );
  }
  
  // ── ADMIN: update a flag 
  updateFlag(payload: Partial<FeatureFlag>): Observable<FeatureFlag> {
    const cmd = new Command();
    cmd.procedure = 'cmdFeaturesEdit';
    cmd.addParameter('FeatureID',     payload.featureID);
    cmd.addParameter('FeatureName',   payload.featureName);
    cmd.addParameter('Branch',        payload.branch   ?? null);
    cmd.addParameter('WorkPackageID', payload.workPackageID ?? null);
    cmd.addParameter('Status',        payload.status   ?? 'Active');

    return this.http
      .post<FeatureFlag>(this.buildUrl(cmd.procedure), cmd)
      .pipe(
        map(res => this.normalise(Array.isArray(res) ? res[0] : res)),
        catchError(err => throwError(() => err))
      );
  }

  // ── ADMIN: delete a flag 
  deleteFlag(featureID: number | string): Observable<any> {
    const cmd = new Command();
    cmd.procedure = 'cmdFeaturesDelete';
    cmd.addParameter('FeatureID', featureID);

    return this.http
      .post(this.buildUrl(cmd.procedure), cmd)
      .pipe(
        map((res: any) => this.normalise(res)),
        catchError(err => throwError(() => err))
      );
  }

}